import { orgAddressFragment } from 'apollo/fragments';
import { clientContentFragment, invoiceSettingsFragment } from 'apollo/fragments/organization';
import { gql } from 'graphql.macro';

export const modifyClientContactSettingsTemplate = gql`
    mutation modifyClientContactSettings(
        $clientId: String!
        $name: String!
        $organizationNumber: String
        $contact: OrganizationContactInput!
    ) {
        modifyClientContactSettings(
            clientId: $clientId
            name: $name
            organizationNumber: $organizationNumber
            contact: $contact
        ) {
            _id
            clientContent {
                name
                contact {
                    address {
                        ...orgAddressFields
                    }
                    communication {
                        phone
                        email
                        site
                    }
                }
            }
        }
    }
    ${orgAddressFragment}
`;

export const modifyInvoiceEnabledTemplate = gql`
    mutation modifyInvoiceEnabled($clientId: String!, $invoiceEnabled: Boolean!) {
        modifyInvoiceEnabled(clientId: $clientId, invoiceEnabled: $invoiceEnabled) {
            _id
            invoiceEnabled
        }
    }
`;

export const activateInactivateClientTemplate = gql`
    mutation activateInactivateClient($clientId: String!) {
        activateInactivateClient(clientId: $clientId) {
            _id
            active
        }
    }
`;

export const updateTransportAndDeliverySettingsTemplate = gql`
    mutation updateTransportAndDeliverySettings(
        $customerId: String!
        $internalDeliveryDaysAdvance: Int
        $externalDeliveryDaysAdvance: Int
        $transportProvider: TransportProviderInput
        $deliveryInstructions: String
        $clinicTransportPhone: String
    ) {
        updateTransportAndDeliverySettings(
            customerId: $customerId
            internalDeliveryDaysAdvance: $internalDeliveryDaysAdvance
            externalDeliveryDaysAdvance: $externalDeliveryDaysAdvance
            transportProvider: $transportProvider
            deliveryInstructions: $deliveryInstructions
            clinicTransportPhone: $clinicTransportPhone
        ) {
            _id
            internalDeliveryDaysAdvance
            externalDeliveryDaysAdvance
            deliveryInstructions
            transportProvider {
                externalId
                code
                channel
            }
        }
    }
`;

export const savePtjCustomerNumberTemplate = gql`
    mutation savePtjCustomerNumber($customerId: MongoDbObjectId!, $ptjCustomerNumber: String!) {
        savePtjCustomerNumber(customerId: $customerId, ptjCustomerNumber: $ptjCustomerNumber) {
            _id
            customerId
            ptjCustomerNumber
        }
    }
`;

export const addWhiteLabelClinicTemplate = gql`
    mutation addWhiteLabelClinic($labId: MongoDbObjectId!, $clinic: ClinicInput!) {
        addWhiteLabelClinic(labId: $labId, clinic: $clinic) {
            _id
            name
        }
    }
`;

export const connectWhiteLabelClinicToLabTemplate = gql`
    mutation connectWhiteLabelClinicToLab($labId: MongoDbObjectId!, $clinicId: MongoDbObjectId!) {
        connectWhiteLabelClinicToLab(labId: $labId, clinicId: $clinicId) {
            _id
            name
        }
    }
`;

export const connectSupplierToLabTemplate = gql`
    mutation connectSupplierToLab($supplierId: MongoDbObjectId!) {
        connectSupplierToLab(supplierId: $supplierId) {
            _id
            name
        }
    }
`;

export const connectLabCustomerTemplate = gql`
    mutation connectLabCustomer($customerId: MongoDbObjectId!) {
        connectLabCustomer(customerId: $customerId) {
            _id
            name
        }
    }
`;

export const connectPtjClinicToLabTemplate = gql`
    mutation connectPtjClinicToLab($labId: MongoDbObjectId!, $clinicId: MongoDbObjectId!) {
        connectPtjClinicToLab(labId: $labId, clinicId: $clinicId) {
            _id
            name
        }
    }
`;

export const addPriceListToClientTemplate = gql`
    mutation addPriceListToClient(
        $clientId: String!
        $actionList: ActionPriceListInput
        $materialList: MaterialPriceListInput
    ) {
        addPriceListToClient(clientId: $clientId, actionList: $actionList, materialList: $materialList) {
            _id
        }
    }
`;

export const addMaterialPriceListToClientTemplate = gql`
    mutation addMaterialPriceListToClient($clientId: String!, $materialList: MaterialPriceListInput!) {
        addMaterialPriceListToClient(clientId: $clientId, materialList: $materialList) {
            _id
        }
    }
`;

export const modifyOverriddenContactTemplate = gql`
    mutation modifyOverriddenContact(
        $clientId: String!
        $isContactOverridden: Boolean!
        $overriddenContact: OrganizationContactInput!
    ) {
        modifyOverriddenContact(
            clientId: $clientId
            isContactOverridden: $isContactOverridden
            overriddenContact: $overriddenContact
        ) {
            _id
            clientContent {
                ...clientContentFields
            }
        }
    }
    ${clientContentFragment}
`;

export const editActionPriceTemplate = gql`
    mutation editActionPrice(
        $clinicId: MongoDbObjectId!
        $labId: MongoDbObjectId!
        $actionId: MongoDbObjectId!
        $price: Float!
        $tiotandvardPrice: Float
        $materialGroup: MaterialGroupInput
        $actionEnabled: Boolean!
        $deliveryTime: Int!
    ) {
        editActionPrice(
            clinicId: $clinicId
            labId: $labId
            actionId: $actionId
            price: $price
            tiotandvardPrice: $tiotandvardPrice
            materialGroup: $materialGroup
            actionEnabled: $actionEnabled
            deliveryTime: $deliveryTime
        ) {
            _id
            action
            price
            tiotandvardPrice
            deliveryTime
        }
    }
`;

export const saveAccountManagerTemplate = gql`
    mutation saveAccountManager(
        $clientId: MongoDbObjectId!
        $accountManager: MongoDbObjectId
        $clinicInternalNote: String
    ) {
        saveAccountManager(
            clientId: $clientId
            accountManager: $accountManager
            clinicInternalNote: $clinicInternalNote
        ) {
            _id
            accountManager
        }
    }
`;

export const saveSettingsTemplate = gql`
    mutation saveSettings($clientId: MongoDbObjectId!, $settings: SettingsInput!) {
        saveSettings(clientId: $clientId, settings: $settings) {
            _id
            settings {
                sendDeliveryNotesByEmail
                deliveryNotesEmailType
                sharedEmails
            }
        }
    }
`;

export const modifyClientLocaleFormatTemplate = gql`
    mutation modifyClientLocaleFormat($clientId: MongoDbObjectId!, $input: LocaleFormatInput) {
        modifyClientLocaleFormat(clientId: $clientId, input: $input) {
            _id
            localeFormat {
                language
                date
                number
            }
        }
    }
`;

export const toggleCustomerEnabledTemplate = gql`
    mutation toggleCustomerEnabled($customerId: MongoDbObjectId!, $isEnabled: Boolean!) {
        toggleCustomerEnabled(customerId: $customerId, isEnabled: $isEnabled) {
            _id
            isActive
        }
    }
`;

export const modifyInvoiceSettingsTemplate = gql`
    mutation modifyInvoiceSettings($clientId: String!, $settings: UnifiedInvoiceSettingsInput!) {
        modifyInvoiceSettings(clientId: $clientId, settings: $settings) {
            _id
            useOwnInvoiceSettings
            invoiceEnabled
            clientContent {
                ...clientContentFields
            }
            invoiceSettings {
                ...invoiceSettingsFields
            }
        }
    }
    ${clientContentFragment}
    ${invoiceSettingsFragment}
`;
